import { getStockImages, StockImage } from "api/media-library";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Title } from "ui/alert/alert";
import { LoadingCover } from "ui/cover";
import { CollectionFilesList } from "../../collection-files-list";
import { PickerProps } from "../media-picker";
import { TitleBar } from "../components/common";
import { stockImageToCollectionFile } from "../helper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function StocksPicker(props: PickerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [searchResult, setSearchResult] = useState(initialQuery);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [images, setImages] = useState<StockImage[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 20;

  // Load images when search changes
  useEffect(() => {
    if (searchResult) {
      loadImages(1, true);
    }
  }, [searchResult]);

  // Load initial search from URL on mount
  useEffect(() => {
    if (initialQuery && !searchResult) {
      setSearchResult(initialQuery);
    }
  }, []);

  const loadImages = async (pageNumber: number, reset: boolean = false) => {
    const loadingState = pageNumber === 1 ? setIsLoading : setIsLoadingMore;
    loadingState(true);

    try {
      const response = await getStockImages(searchResult, pageNumber, limit);

      if (reset) {
        setImages(response.data);
      } else {
        setImages((prev) => [...prev, ...response.data]);
      }

      setPage(pageNumber);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error("Failed to load stock images:", error);
    } finally {
      loadingState(false);
    }
  };

  const onSearch = () => {
    setSearchResult(query);
    setSearchParams({ q: query });
    setPage(1);
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && page < totalPages) {
      loadImages(page + 1, false);
    }
  };

  const allImagesLoaded = page >= totalPages && totalPages > 0;
  const files = images.map(stockImageToCollectionFile);

  return (
    <LoadingCover isLoading={isLoading}>
      <div className="flex items-center gap-3 my-5 max-w-md">
        <div className="relative flex-1">
          <i className="fa-regular fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
            placeholder="Search Pexels stock images..."
            className="h-[38px] pl-9 border-neutral-200 hover:border-neutral-300 focus-visible:border-blue-500 focus-visible:ring-blue-500/20"
          />
        </div>
        <Button
          onClick={onSearch}
          disabled={!query.trim()}
          variant="outline"
          className="h-[38px] px-4 text-sm font-medium border-neutral-200 hover:border-neutral-300"
        >
          Search
        </Button>
      </div>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
          <p className="font-inter text-sm text-neutral-600">
            Searching Pexels...
          </p>
        </div>
      ) : !searchResult ? (
        <div className="text-center py-12 min-h-[200px] flex flex-col items-center justify-center">
          <i className="fa-regular fa-image text-4xl text-neutral-300 mb-4"></i>
          <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
            Search for stock images
          </h3>
          <p className="font-inter text-sm text-neutral-600 mb-4">
            Free high-quality photos from Pexels
          </p>
          <p className="font-inter text-xs text-neutral-500">
            Try: "nature", "business", "technology", "food"
          </p>
        </div>
      ) : images.length < 1 ? (
        <div className="text-center py-12 min-h-[200px] flex flex-col items-center justify-center">
          <i className="fa-regular fa-search text-4xl text-neutral-300 mb-4"></i>
          <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
            No results found
          </h3>
          <p className="font-inter text-sm text-neutral-600">
            No results for "{searchResult}". Try different keywords.
          </p>
        </div>
      ) : (
        <>
          <TitleBar>
            <Title>Stock images</Title>
          </TitleBar>
          <CollectionFilesList
            files={files}
            selectedMediaSet={props.selectedMediaFiles}
            selectedCollection={props.selectedCollection}
            onSelect={props.handleFileAdd}
            onRemove={props.handleFileRemove}
          />

          {/* Load More Button */}
          {files.length > 0 && (
            <div className="flex flex-col items-center justify-center mt-6 mb-4">
              {allImagesLoaded ? (
                <p className="font-inter text-sm text-neutral-500">
                  All images loaded
                </p>
              ) : (
                <Button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  variant="outline"
                  size="sm"
                  className="border-neutral-200 hover:border-neutral-300"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </LoadingCover>
  );
}
